import axios from 'axios';
import axiosClean from './axiosInterceptor';
import { jwtDecode } from 'jwt-decode';

const LUCCI_TOKEN = import.meta.env.VITE_LUCCI_SAAS_TOKEN;

const USER_URL = '/user';

// Helper to extract the phone from token
const getPhoneFromToken = (token) => {
  try {
    const decodedToken = jwtDecode(token);
    return decodedToken.phone || null;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

// Helper to get id from localStorage
const getIdFromLocalStorage = () => {
  try {
    const id =
      localStorage.getItem('sender_id') || sessionStorage.getItem('sender_id');
    return id ? Number(id) : null;
  } catch (error) {
    console.error('Error retrieving user id:', error);
    return null;
  }
};

//:: HTTP GET
const fetchUserProfile = async (token) => {
  try {
    const phone = getPhoneFromToken(token);
    const response = await axios.get(`${USER_URL}/profile?phone=${phone}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

//:: HTTP DELETE
const deleteUserAccount = async (token) => {
  try {
    const phone = getPhoneFromToken(token);
    const response = await axios.delete(`${USER_URL}/delete?phone=${phone}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting user account:', error);
    throw error;
  }
};

//:: HTTP PATCH
const updateUserProfile = async (token, data) => {
  try {
    const userId = getIdFromLocalStorage();
    const phone = getPhoneFromToken(token);

    const filteredData = Object.fromEntries(
      Object.entries({
        id: userId,
        phone,
        ...data,
      }).filter(
        ([_, value]) => value !== undefined && value !== null && value !== ''
      )
    );

    const urlParams = new URLSearchParams(filteredData).toString();
    const url = `${USER_URL}/update?${urlParams}`;

    const response = await axios.patch(url, null);
    return response.data;
  } catch (error) {
    console.error(
      'Error updating user profile:',
      error.response?.data || error.message
    );
    throw new Error(
      error.response?.data?.message || 'Update user profile failed.'
    );
  }
};

//:: HTTP GET - Request verification code
const requestPhoneVerificationCode = async (oldPhone, newPhone) => {
  try {
    const response = await axios.get(
      `${USER_URL}/new_code?old_phone=${oldPhone}&new_phone=${newPhone}`
    );
    return response.data;
  } catch (error) {
    console.error(
      'Error requesting verification code:',
      error.response?.data || error.message
    );
    throw new Error(
      error.response?.data?.message || 'Failed to request verification code.'
    );
  }
};

//:: HTTP GET - Verify code and change phone number
const verifyPhoneChange = async (oldPhone, newPhone, code) => {
  try {
    const response = await axios.get(
      `${USER_URL}/new_phone?old_phone=${oldPhone}&new_phone=${newPhone}&code=${code}`
    );
    return response.data;
  } catch (error) {
    console.error(
      'Error verifying phone change:',
      error.response?.data || error.message
    );
    throw new Error(
      error.response?.data?.message || 'Failed to verify phone change.'
    );
  }
};

const requestNewVerificationCode = async (phone) => {
  try {
    const response = await axiosClean.get(
      `${USER_URL}/password/send_code?phone=${phone}`,
      {
        headers: {
          Authorization: `Bearer ${LUCCI_TOKEN}`,
        },
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

const changePassword = async (phone, code, newPassword) => {
  try {
    const response = await axiosClean.get(
      `${USER_URL}/passoword/new_password?phone=${phone}&code=${code}&password=${encodeURIComponent(newPassword)}`,
      {
        headers: {
          Authorization: `Bearer ${LUCCI_TOKEN}`,
        },
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//:: HTTP PATCH - Admin update another user's details
const updateUserDetails = async (userId, userPhone, fullname) => {
  try {
    const params = new URLSearchParams(
      Object.fromEntries(
        Object.entries({ id: userId, phone: userPhone, fullname }).filter(
          ([_, value]) => value !== undefined && value !== null && value !== ''
        )
      )
    ).toString();

    const response = await axios.patch(`${USER_URL}/update?${params}`, null);
    return response.data;
  } catch (error) {
    console.error('Error updating user details:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Update user failed.');
  }
};

export {
  fetchUserProfile,
  deleteUserAccount,
  updateUserProfile,
  updateUserDetails,
  requestPhoneVerificationCode,
  verifyPhoneChange,
  requestNewVerificationCode,
  changePassword,
};
